import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';
import _ from 'underscore';

import isArchived from 'archive/isArchived';
import {ConnectionSchema} from 'instruments/connection-schemas/ConnectionSchemaConstants';
import AbsoluteDropdownContainer from 'ui-library/AbsoluteDropdownContainer';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Menu from 'ui-library/menus/Menu';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import runModal from 'ui-library/runModal';

import CreateConnectionModal from '../CreateConnectionModal';

interface NewConnectionDropdownProps {
  connectionSchemas: Array<ConnectionSchema>;
}

export default function NewConnectionDropdown(props: NewConnectionDropdownProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const hasSchemas = props.connectionSchemas.length > 0;

  // We have designs for a menu with the non-instrument-specific File Watcher adapter in a separate section,
  // but no backend concept of Connection Type yet, hardcoding this for now until we have more examples and
  // can make a more-informed choice on how to handle types more generally in the domain model
  enum ConnectionType {
    GENERIC = 'generic',
    INSTRUMENT = 'instrument',
  }
  function getConnectionType(schema: ConnectionSchema): ConnectionType {
    return schema.integrationAdapterId === 'file-watcher-adapter'
      ? ConnectionType.GENERIC
      : ConnectionType.INSTRUMENT;
  }

  const connectionSchemas = _.sortBy(
    props.connectionSchemas.filter((schema) => !isArchived(schema)),
    getConnectionType
  );

  function onSelect(connectionSchema: ConnectionSchema): void {
    runModal(CreateConnectionModal, {connectionSchema});
  }
  const menu = hasSchemas ? (
    <SearchableMenu
      scrollable={true}
      className={css(styles.pullRight)}
      width={Menu.WIDTHS.MEDIUM}
      height={Menu.HEIGHTS.MEDIUM}
      onSelect={onSelect}
      optionDatas={connectionSchemas.map((schema) => ({
        name: schema.name,
        value: schema,
      }))}
    />
  ) : null;

  return (
    <div className="u-display-inline-block dropdown">
      <AbsoluteDropdownContainer
        onRequestClose={() => {
          setIsOpen(false);
        }}
        open={isOpen}
        menu={menu}
        shouldCloseOnInsideClick={true}
      >
        <Tooltip
          title={hasSchemas ? 'Create...' : 'Loading connection options...'}
          placement={TooltipPlacement.BOTTOM}
          container="body"
        >
          <Button
            buttonStyle={ButtonStyle.TERTIARY_BLUE}
            disabled={!hasSchemas}
            icon="compose"
            onClick={() => {
              setIsOpen(true);
            }}
            size={ButtonSize.SMALL}
          />
        </Tooltip>
      </AbsoluteDropdownContainer>
    </div>
  );
}

const styles = StyleSheet.create({
  pullRight: {
    right: 0,
  },
});
